import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import type { Pose } from '../types';
import { api } from '../api/client';
import { downscaleToJpeg } from '../lib/image';
import { usePoseImageOverride, usePoseImagesRefresh } from '../data/poseImages';

type Phase = 'idle' | 'generating' | 'ready' | 'error' | 'saving';

/**
 * Admin-only: describe what's wrong with a pose's image, generate a new one with
 * gpt-image-1, compare, and replace the live image if it's better.
 */
export function PoseImageEditor({ pose }: { pose: Pose }) {
  const override = usePoseImageOverride(pose.id);
  const refresh = usePoseImagesRefresh();
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [candidate, setCandidate] = useState<{ url: string; key: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refPhoto, setRefPhoto] = useState<string | null>(null); // downscaled JPEG data URL
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const onPickPhoto = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the admin re-pick the same file later
    if (!file) return;
    try {
      setRefPhoto(await downscaleToJpeg(file));
    } catch {
      setError('Could not read that image — try a different photo.');
    }
  };

  const stopPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => stopPoll, []);

  const currentSrc = override ?? `${import.meta.env.BASE_URL}poses/${pose.id}.webp`;
  const busy = phase === 'generating' || phase === 'saving';

  const generate = async () => {
    setError(null);
    setCandidate(null);
    setPhase('generating');
    try {
      const { jobId } = await api.createImageJob(
        pose.id,
        feedback.trim(),
        {
          nameEnglish: pose.nameEnglish,
          description: pose.description,
          cues: pose.cues,
        },
        refPhoto ?? undefined,
      );
      let tries = 0;
      stopPoll();
      pollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const job = await api.getImageJob(pose.id, jobId);
          if (job.status === 'done' && job.candidateUrl && job.candidateKey) {
            stopPoll();
            setCandidate({ url: job.candidateUrl, key: job.candidateKey });
            setPhase('ready');
          } else if (job.status === 'error') {
            stopPoll();
            setError(job.message || 'Generation failed.');
            setPhase('error');
          } else if (tries > 50) {
            stopPoll();
            setError('Timed out waiting for the image.');
            setPhase('error');
          }
        } catch {
          stopPoll();
          setError('Lost connection while generating.');
          setPhase('error');
        }
      }, 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start generation.');
      setPhase('error');
    }
  };

  const approve = async () => {
    if (!candidate) return;
    setPhase('saving');
    try {
      await api.approvePoseImage(pose.id, candidate.key);
      refresh();
      setCandidate(null);
      setFeedback('');
      setRefPhoto(null);
      setPhase('idle');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the image.');
      setPhase('error');
    }
  };

  const revert = async () => {
    setPhase('saving');
    try {
      await api.revertPoseImage(pose.id);
      refresh();
      setPhase('idle');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not revert.');
      setPhase('error');
    }
  };

  return (
    <section className="admin-panel">
      <button
        type="button"
        className="admin-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        ✨ Admin · improve image {open ? '▲' : '▼'}
      </button>

      {open && (
        <div className="admin-body">
          <label className="setting-label" htmlFor="img-feedback">
            What's wrong with the current image?
          </label>
          <textarea
            id="img-feedback"
            className="admin-feedback"
            rows={3}
            placeholder="e.g. the front knee should be bent to 90°, and both arms extended straight out to the sides at shoulder height"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            disabled={busy}
          />

          <div className="ref-photo">
            {refPhoto ? (
              <div className="ref-thumb">
                <img src={refPhoto} alt="reference position" />
                <button
                  type="button"
                  className="ref-remove"
                  onClick={() => setRefPhoto(null)}
                  disabled={busy}
                  aria-label="Remove reference photo"
                >
                  ×
                </button>
              </div>
            ) : (
              <label className="ref-add">
                📷 Add reference photo
                <input type="file" accept="image/*" onChange={onPickPhoto} disabled={busy} hidden />
              </label>
            )}
            <span className="ref-hint">
              Optional — a photo of the correct position helps the AI get complex poses right.
            </span>
          </div>

          <div className="admin-actions">
            <button type="button" className="btn primary" onClick={generate} disabled={busy}>
              {phase === 'generating' ? 'Generating…' : 'Generate new image'}
            </button>
            {override && (
              <button type="button" className="btn" onClick={revert} disabled={busy}>
                Revert to original
              </button>
            )}
          </div>

          {phase === 'generating' && (
            <p className="muted">gpt-image-1 is drawing — this takes around half a minute.</p>
          )}
          {error && <p className="auth-error">{error}</p>}

          {candidate && (
            <>
              <div className="candidate-compare">
                <figure>
                  <figcaption className="section-label">Current</figcaption>
                  <img className="candidate-img" src={currentSrc} alt="current pose" />
                </figure>
                <figure>
                  <figcaption className="section-label">New</figcaption>
                  <img className="candidate-img" src={candidate.url} alt="newly generated pose" />
                </figure>
              </div>
              <div className="admin-actions">
                <button type="button" className="btn primary" onClick={approve} disabled={busy}>
                  Use this image
                </button>
                <button type="button" className="btn" onClick={generate} disabled={busy}>
                  Regenerate
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setCandidate(null);
                    setPhase('idle');
                  }}
                  disabled={busy}
                >
                  Discard
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
